import React from "react";
import { X, Copy, Facebook, MessageCircle, Twitter, Send, Share2 } from "lucide-react";
import { toast } from "sonner";

const SITE = typeof window !== "undefined" ? window.location.origin : "";

export const ShareModal = ({ open, onClose, title, text, path }) => {
  if (!open) return null;
  const url = `${SITE}${path}`;
  const enc = encodeURIComponent;
  const tText = text || title || "Découvrez ça sur Voamada 🎵";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
      onClose();
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || "Voamada", text: tText, url });
        onClose();
      } catch {}
    } else {
      copyLink();
    }
  };

  const links = [
    { label: "WhatsApp", color: "#25D366", icon: <MessageCircle size={18} />, href: `https://wa.me/?text=${enc(`${tText} ${url}`)}` },
    { label: "Facebook", color: "#1877F2", icon: <Facebook size={18} />, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { label: "Messenger", color: "#0084FF", icon: <MessageCircle size={18} />, href: `https://www.facebook.com/dialog/send?link=${enc(url)}&app_id=0&redirect_uri=${enc(url)}` },
    { label: "Twitter/X", color: "#1DA1F2", icon: <Twitter size={18} />, href: `https://twitter.com/intent/tweet?text=${enc(tText)}&url=${enc(url)}` },
    { label: "Telegram", color: "#26A5E4", icon: <Send size={18} />, href: `https://t.me/share/url?url=${enc(url)}&text=${enc(tText)}` },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-md bg-brand-surface border border-white/10 rounded-2xl p-6 animate-[fade-up_0.25s_ease-out]"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-black flex items-center gap-2">
            <Share2 size={18} /> Partager
          </h3>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="share-close">
            <X size={20} />
          </button>
        </div>
        {title && <p className="text-sm text-white/70 mb-5 truncate">{title}</p>}

        <div className="grid grid-cols-5 gap-3 mb-5">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-white/5 transition-colors"
              data-testid={`share-${l.label.toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: l.color }}>
                {l.icon}
              </div>
              <span className="text-[10px] text-white/70">{l.label}</span>
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 mb-3">
          <span className="text-xs text-white/50 truncate flex-1">{url}</span>
          <button onClick={copyLink} className="text-brand-red hover:text-brand-redHover text-xs font-semibold inline-flex items-center gap-1" data-testid="share-copy">
            <Copy size={12} /> Copier
          </button>
        </div>

        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare} className="btn-ghost w-full text-sm inline-flex items-center justify-center gap-2" data-testid="share-native">
            <Share2 size={14} /> Plus d'options
          </button>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
